import { useLoaderData, useLocation } from '@remix-run/react';
import { useEffect, useState } from 'react';
import { Header } from '~/components/header/Header';
import { showPaymentRenewalToast } from '~/components/toasts/renewal';
import type { TokenStats } from '~/lib/.server/rateLimit';
import { sendAnalyticsEvent } from '~/lib/analytics.client';
import type { ProjectInfo } from '~/lib/constants';
import { GithubIntegration } from '~/lib/context/github';
import { useLogin } from '~/lib/login';
import { importModifications } from '~/lib/projects/importStore';
import { settingsService } from '~/lib/services/settings-service';
import { pricingStore } from '~/lib/stores/pricing/pricing';
import { userStore } from '~/lib/stores/user';
import { AnalyticsAction, AnalyticsTrackEvent } from '~/types/analytics';
import type { ProjectGithubIntegration } from '~/types/github';
import type { UserData } from '~/types/user';
import { captureTrackingParams, sendAdConversions } from '~/utils/tracking-params.client';

let autoLoginStarted = false;

export interface IndexLoaderData {
  id?: string;
  slug?: string;
  projectId?: string;
  projectInfo?: ProjectInfo;
  loggedIn: boolean;
  user?: UserData | null;
  tokenStats?: TokenStats;
  github?:
    | {
        linked?: boolean;
        data?: undefined;
      }
    | {
        linked: true;
        data: ProjectGithubIntegration;
      };
}

export function IndexLayout({ children }: { children: React.ReactNode }) {
  const { user: initialUser, loggedIn, github } = useLoaderData<IndexLoaderData>();

  const location = useLocation();

  const { login } = useLogin();

  const [autoAuth] = useState(() => {
    const params = new URLSearchParams(location.search);

    return params.get('autoAuth') !== null;
  });

  useEffect(() => {
    if (!autoAuth) {
      return;
    }

    const url = new URL(window.location.href);

    url.searchParams.delete('autoAuth');

    if (loggedIn) {
      window.history.replaceState({}, '', url);
    } else if (!autoLoginStarted) {
      autoLoginStarted = true;
      login(url.toString().slice(url.origin.length));
    }
  }, [autoAuth]);

  useEffect(() => {
    if (initialUser) {
      settingsService.init(initialUser.settings);
    }

    userStore.init(initialUser, loggedIn);
  }, [initialUser]);

  useEffect(() => {
    const user = userStore.user.get();

    if (user && user.activeOrganizationId) {
      const organization = user.organizations?.find((org) => org.id === user.activeOrganizationId);

      if (organization?.billing && !organization.billing.paid) {
        showPaymentRenewalToast(organization?.role === 'admin');
      }
    } else if (user && user.membership?.subscription?.status === 'past_due' && !pricingStore.showPricingOverlay.get()) {
      showPaymentRenewalToast(true);
    }
  }, []);

  const githubInitialData = github?.linked && 'data' in github ? github.data : undefined;

  useEffect(() => {
    captureTrackingParams();

    if (loggedIn) {
      sendAdConversions().catch(console.error);
    }
  }, []);

  function handleCloned(error: any, cloned: boolean) {
    if (error) {
      importModifications.reject(error);
    } else {
      importModifications.resolve();
    }

    if (cloned) {
      sendAnalyticsEvent({
        action: AnalyticsAction.Track,
        payload: {
          event: error ? AnalyticsTrackEvent.GithubRepoCloneFailed : AnalyticsTrackEvent.GithubRepoCloned,
        },
      });
    }
  }

  return (
    <GithubIntegration initial={githubInitialData} onCloned={handleCloned} initialUser={initialUser ?? undefined}>
      {(loggedIn || !autoAuth) && (
        <div className="flex flex-col h-full w-full">
          <Header />
          {children}
        </div>
      )}
    </GithubIntegration>
  );
}
